import { useState, useEffect, useCallback, useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { FaClipboardList, FaEdit, FaTrash } from "react-icons/fa";
import { CUENTAS_ER } from "../../constants/clasificacionContable";
import { useAuthContext } from "../../context/auth";
import { getProveedores, crearProveedor, actualizarProveedor } from "../../utils/proveedores";
import { client } from "../../utils/supabase";
import { useCurrency } from "../../utils/useCurrency";
import { Header } from "../Header";
import styles from "./GestionProveedores.module.scss";

const FRECUENCIAS = ["mensual", "trimestral", "anual"];
const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
const formatRut = (rut) => {
  if (!rut) return "—";
  const clean = rut.replace(/\./g, "").trim();
  const parts = clean.split("-");
  if (parts.length !== 2) return rut;
  const num = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${num}-${parts[1]}`;
};
const MONEDAS = ["CLP", "USD", "PEN", "UF"];

const GestionProveedores = () => {
  const { pais, id: userId } = useAuthContext();
  const { formatAmount } = useCurrency();
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({});
  const [showEstadoCuenta, setShowEstadoCuenta] = useState(false);
  const [estadoCuentaProveedor, setEstadoCuentaProveedor] = useState(null);
  const [estadoCuentaData, setEstadoCuentaData] = useState([]);

  const cargar = useCallback(async () => {
    if (!pais || pais === "Region") return;
    setLoading(true);
    try {
      const data = await getProveedores(pais);
      setProveedores(data);
    } catch {
      toast.error("Error cargando proveedores");
    } finally {
      setLoading(false);
    }
  }, [pais]);

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargar]);

  const proveedoresFiltrados = useMemo(
    () =>
      proveedores.filter((p) => {
        if (
          busqueda &&
          !p.nombre?.toLowerCase().includes(busqueda.toLowerCase()) &&
          !p.alias?.toLowerCase().includes(busqueda.toLowerCase())
        )
          return false;
        if (filtroTipo === "recurrente" && !p.es_recurrente) return false;
        if (filtroTipo === "extranjero" && !p.es_extranjero) return false;
        if (filtroTipo === "factoring" && !p.es_factoring) return false;
        return true;
      }),
    [proveedores, busqueda, filtroTipo]
  );

  const handleNuevo = () => {
    setEditando(null);
    setForm({
      nombre: "",
      alias: "",
      rut_ruc: "",
      pais,
      es_extranjero: false,
      es_recurrente: false,
      es_factoring: false,
      habilitado: true,
      contacto_nombre: "",
      contacto_email: "",
      contacto_telefono: "",
      notas: "",
      alias_banco: "",
      cuenta_er_default: "",
      tipo_contable_default: "",
      monto_recurrente: "",
      moneda_recurrente: "CLP",
      dia_pago: 30,
      frecuencia: "mensual",
    });
    setShowModal(true);
  };

  const handleEditar = (p) => {
    setEditando(p.id);
    setForm({ ...p });
    setShowModal(true);
  };

  const handleGuardar = async () => {
    if (!form.nombre) return toast.error("El nombre es obligatorio");
    try {
      if (editando) {
        await actualizarProveedor(editando, form);
        toast.success("Proveedor actualizado");
      } else {
        await crearProveedor({ ...form, pais });
        toast.success("Proveedor creado");
      }
      setShowModal(false);
      await cargar();
    } catch {
      toast.error("Error al guardar");
    }
  };

  const handleEliminar = async (proveedor) => {
    try {
      const { count: countFacturas } = await client
        .from("facturas_sii_recibidas")
        .select("id", { count: "exact", head: true })
        .eq("proveedor_id", proveedor.id);

      const { count: countMovimientos } = await client
        .from("facturas_proveedor")
        .select("id", { count: "exact", head: true })
        .eq("proveedor_id", proveedor.id);

      const tieneVinculos = (countFacturas || 0) + (countMovimientos || 0) > 0;

      if (tieneVinculos && userId !== 1 && userId !== 4) {
        toast.error("Este proveedor tiene facturas asociadas. Solo Ignacio puede eliminarlo.");
        return;
      }

      const confirmMsg = tieneVinculos
        ? `"${proveedor.alias || proveedor.nombre}" tiene facturas asociadas. ¿Confirmar eliminación?`
        : `¿Eliminar "${proveedor.alias || proveedor.nombre}"?`;

      if (!window.confirm(confirmMsg)) return;

      if (tieneVinculos && !window.confirm("Segunda confirmación requerida. ¿Estás seguro?")) return;

      await client.from("proveedores").update({ habilitado: false }).eq("id", proveedor.id);
      toast.success("Proveedor deshabilitado");
      await cargar();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const handleVerEstadoCuenta = async (p) => {
    setEstadoCuentaProveedor(p);
    setShowEstadoCuenta(true);
    try {
      const { data } = await client
        .from("facturas_sii_recibidas")
        .select("id, folio, fecha_emision, monto_total, estado_vinculacion, plazopago")
        .eq("proveedor_id", p.id)
        .order("fecha_emision", { ascending: false });
      setEstadoCuentaData(data || []);
    } catch {
      toast.error("Error cargando estado de cuenta");
    }
  };

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  return (
    <div className={styles.wrapper}>
      <Header title="Proveedores">
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Buscar razón social o alias..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            style={{ width: 240 }}
          />
          <select
            className="form-select form-select-sm"
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            style={{ width: 160 }}
          >
            <option value="">Todos</option>
            <option value="recurrente">Con mensualidad</option>
          </select>
          <button className="btn btn-sm btn-primary" onClick={handleNuevo}>
            + Proveedor
          </button>
        </div>
      </Header>

      <div className={styles.content}>
        {loading ? (
          <div className={styles.empty}>Cargando...</div>
        ) : (
          <table className="table table-bordered table-striped table-hover table-sm">
            <thead className="table-dark">
              <tr>
                <th style={{ width: "30%" }}>Razón social</th>
                <th style={{ width: "14%" }}>Alias</th>
                <th className="text-center" style={{ width: "12%" }}>
                  {pais === "Chile" ? "RUT" : "RUC"}
                </th>
                <th style={{ width: "16%" }}>Cuenta ER</th>
                <th className="text-center" style={{ width: "8%" }}>
                  Día pago
                </th>
                <th className="text-end" style={{ width: "10%" }}>
                  Monto
                </th>
                <th style={{ width: "6%" }} />
              </tr>
            </thead>
            <tbody>
              {proveedoresFiltrados.length === 0 && (
                <tr>
                  <td colSpan={7} className={styles.empty}>
                    Sin proveedores
                  </td>
                </tr>
              )}
              {proveedoresFiltrados.map((p) => (
                <tr key={p.id}>
                  <td className="text-start text-capitalize">{(p.nombre || "").toLowerCase()}</td>
                  <td className="text-start" style={{ color: p.alias ? "inherit" : "#ccc", fontStyle: "italic" }}>
                    {p.alias || "Sin alias"}
                  </td>
                  <td className="text-center">{formatRut(p.rut_ruc)}</td>
                  <td style={{ fontSize: "0.78rem" }}>
                    {p.cuenta_er_default ? (
                      CUENTAS_ER.find((c) => c.value === p.cuenta_er_default)?.label || p.cuenta_er_default
                    ) : (
                      <span style={{ color: "#ccc" }}>Sin asignar</span>
                    )}
                  </td>
                  <td style={{ fontSize: "0.82rem", textAlign: "center" }}>
                    {p.es_recurrente ? `Día ${p.dia_pago}` : "—"}
                  </td>
                  <td style={{ fontSize: "0.82rem", textAlign: "right" }}>
                    {p.es_recurrente ? formatAmount(p.monto_recurrente, p.moneda_recurrente) : "—"}
                  </td>
                  <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                    <FaClipboardList
                      style={{ cursor: "pointer", fontSize: "0.85rem", color: "#0d6efd", marginRight: 8 }}
                      title="Estado de cuenta"
                      onClick={() => handleVerEstadoCuenta(p)}
                    />
                    <FaEdit
                      style={{ cursor: "pointer", fontSize: "0.85rem", marginRight: 8 }}
                      title="Editar"
                      onClick={() => handleEditar(p)}
                    />
                    <FaTrash
                      style={{ cursor: "pointer", fontSize: "0.78rem", color: "#dc3545" }}
                      title="Eliminar"
                      onClick={() => handleEliminar(p)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>
            {editando ? `Editar — ${form.alias || form.nombre}` : "Nuevo proveedor"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label className={styles.label}>Razón social *</label>
              <input
                className="form-control form-control-sm"
                value={form.nombre || ""}
                onChange={(e) => set("nombre", e.target.value)}
                placeholder="Nombre formal completo"
              />
            </div>

            <div>
              <label className={styles.label}>Alias</label>
              <input
                className="form-control form-control-sm"
                value={form.alias || ""}
                onChange={(e) => set("alias", e.target.value)}
                placeholder="Nombre corto para uso interno"
              />
            </div>

            <div>
              <label className={styles.label}>{pais === "Chile" ? "RUT" : "RUC"}</label>
              <input
                className="form-control form-control-sm"
                value={form.rut_ruc || ""}
                onChange={(e) => set("rut_ruc", e.target.value)}
              />
            </div>

            <div>
              <label className={styles.label}>Alias banco (matching Clay)</label>
              <input
                className="form-control form-control-sm"
                value={form.alias_banco || ""}
                onChange={(e) => set("alias_banco", e.target.value)}
                placeholder="Texto que aparece en el banco"
              />
            </div>

            <div>
              <label className={styles.label}>Contacto</label>
              <input
                className="form-control form-control-sm"
                value={form.contacto_nombre || ""}
                onChange={(e) => set("contacto_nombre", e.target.value)}
                placeholder="Nombre"
              />
            </div>

            <div>
              <label className={styles.label}>Email</label>
              <input
                className="form-control form-control-sm"
                type="email"
                value={form.contacto_email || ""}
                onChange={(e) => set("contacto_email", e.target.value)}
              />
            </div>

            <div>
              <label className={styles.label}>Teléfono</label>
              <input
                className="form-control form-control-sm"
                value={form.contacto_telefono || ""}
                onChange={(e) => set("contacto_telefono", e.target.value)}
              />
            </div>

            <div>
              <label className={styles.label}>Cuenta ER default</label>
              <select
                className="form-select form-select-sm"
                value={form.cuenta_er_default || ""}
                onChange={(e) => set("cuenta_er_default", e.target.value)}
              >
                <option value="">Sin asignar</option>
                {CUENTAS_ER.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 20, flexWrap: "wrap" }}>
              {[
                { field: "es_extranjero", label: "Proveedor extranjero" },
                { field: "es_recurrente", label: "Tiene mensualidad" },
                { field: "es_factoring", label: "Empresa de factoring" },
                { field: "habilitado", label: "Habilitado" },
              ].map(({ field, label }) => (
                <label
                  key={field}
                  style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.82rem", cursor: "pointer" }}
                >
                  <input type="checkbox" checked={!!form[field]} onChange={(e) => set(field, e.target.checked)} />
                  {label}
                </label>
              ))}
            </div>

            {form.es_recurrente && (
              <>
                <div>
                  <label className={styles.label}>Monto mensualidad</label>
                  <input
                    className="form-control form-control-sm"
                    type="number"
                    value={form.monto_recurrente || ""}
                    onChange={(e) => set("monto_recurrente", e.target.value)}
                  />
                </div>
                <div>
                  <label className={styles.label}>Moneda</label>
                  <select
                    className="form-select form-select-sm"
                    value={form.moneda_recurrente || "CLP"}
                    onChange={(e) => set("moneda_recurrente", e.target.value)}
                  >
                    {MONEDAS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={styles.label}>Día de pago</label>
                  <input
                    className="form-control form-control-sm"
                    type="number"
                    min={1}
                    max={31}
                    value={form.dia_pago || 30}
                    onChange={(e) => set("dia_pago", parseInt(e.target.value))}
                  />
                </div>
                <div>
                  <label className={styles.label}>Frecuencia</label>
                  <select
                    className="form-select form-select-sm"
                    value={form.frecuencia || "mensual"}
                    onChange={(e) => set("frecuencia", e.target.value)}
                  >
                    {FRECUENCIAS.map((f) => (
                      <option key={f} value={f}>
                        {capitalize(f)}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            <div style={{ gridColumn: "1 / -1" }}>
              <label className={styles.label}>Notas</label>
              <textarea
                className="form-control form-control-sm"
                rows={2}
                value={form.notas || ""}
                onChange={(e) => set("notas", e.target.value)}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" size="sm" onClick={handleGuardar}>
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEstadoCuenta} onHide={() => setShowEstadoCuenta(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>
            Estado de cuenta — {estadoCuentaProveedor?.alias || estadoCuentaProveedor?.nombre}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {estadoCuentaData.length === 0 ? (
            <div style={{ textAlign: "center", color: "#999", padding: 30 }}>Sin facturas registradas</div>
          ) : (
            <table className="table table-bordered table-striped table-sm">
              <thead className="table-dark">
                <tr>
                  <th>Folio</th>
                  <th>Fecha emisión</th>
                  <th>Vencimiento</th>
                  <th style={{ textAlign: "right" }}>Monto</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {estadoCuentaData.map((f) => (
                  <tr key={f.id}>
                    <td>{f.folio}</td>
                    <td>{f.fecha_emision}</td>
                    <td>{f.plazopago || "—"}</td>
                    <td style={{ textAlign: "right" }}>{formatAmount(f.monto_total, "CLP")}</td>
                    <td>
                      <span
                        style={{
                          fontSize: "0.72rem",
                          padding: "2px 6px",
                          borderRadius: 3,
                          background: f.estado_vinculacion === "vinculada" ? "#d4edda" : "#fff3cd",
                          color: f.estado_vinculacion === "vinculada" ? "#155724" : "#856404",
                        }}
                      >
                        {capitalize(f.estado_vinculacion || "pendiente")}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowEstadoCuenta(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GestionProveedores;
